export type SesameCandidate = {
  resolver: string
  oname?: string
  otype?: string
  jpos?: string
  jradeg?: number
  jdedeg?: number
  ids?: string[]
}

export type SesameResolved = {
  name?: string
  resolved?: boolean
  candidates: SesameCandidate[]
}

export type SimbadKind =
  | "STAR"
  | "STAR_MULT"
  | "STAR_VAR"
  | "DSO_GALAXY"
  | "DSO_NEBULA"
  | "DSO_PN"
  | "DSO_OPEN_CLUSTER"
  | "DSO_GLOBULAR_CLUSTER"
  | "DSO_DARK_NEBULA"
  | "DSO_REFLECTION_NEBULA"
  | "DSO_SNR"
  | "DSO_MOL_CLOUD"
  | "DSO_GALAXY_CLUSTER"
  | "DSO_ASTERISM"

export type SimbadResolvedMatch = {
  recordNumber: string
  name: string
  commonName?: string
  kind: SimbadKind
  otype?: string
  raDeg: number
  decDeg: number
  raHms?: string
  decDms?: string
  resolver?: string
}

function isSpacecraftOtype(otype?: string): boolean {
  if (!otype) return false
  return otype.toLowerCase().includes("spacecraft")
}

function pickCommonName(ids?: string[], oname?: string): string | undefined {
  if (!ids?.length) return undefined
  const on = (oname || "").trim().toLowerCase()

  // Prefer IDs that look like human-readable names (contain lowercase letters).
  const fromIds =
    ids.find(id => /[a-z]/.test(id) && id.trim().toLowerCase() !== on) ??
    // Otherwise, prefer something with spaces.
    ids.find(id => id.includes(" ") && id.trim().toLowerCase() !== on)

  const picked = fromIds?.trim()
  if (!picked) return undefined
  if (picked.toLowerCase() === on) return undefined
  return picked
}

/**
 * Browser-friendly Sesame resolver using the machine-readable nph-sesame endpoint.
 * -oxp = XML output
 * /A   = all resolvers
 */
export async function resolveSesame(query: string): Promise<SesameResolved> {
  const trimmed = query.trim()
  if (!trimmed) return { resolved: false, candidates: [] }

  const url = `https://cdsweb.u-strasbg.fr/cgi-bin/nph-sesame/-oxp/A?${encodeURIComponent(
    trimmed,
  )}`
  const resp = await fetch(url, {
    method: "GET",
    headers: { Accept: "application/xml, text/xml;q=0.9, */*;q=0.8" },
  })
  const text = await resp.text()
  return parseSesameOxpXml(text)
}

export function parseSesameOxpXml(xmlText: string): SesameResolved {
  const out: SesameResolved = { candidates: [] }
  try {
    const doc = new DOMParser().parseFromString(xmlText, "application/xml")
    const parseError = doc.querySelector("parsererror")
    if (parseError) return out

    const target = doc.querySelector("Target")
    if (target) {
      out.resolved = true
      const name = target.querySelector("name")?.textContent?.trim()
      if (name) out.name = name
    }

    const resolvers = Array.from(doc.querySelectorAll("Resolver"))
    for (const r of resolvers) {
      const resolverName = r.getAttribute("name") || "unknown"
      const oname = r.querySelector("oname")?.textContent?.trim() || undefined
      const otype = r.querySelector("otype")?.textContent?.trim() || undefined
      const jpos = r.querySelector("jpos")?.textContent?.trim() || undefined

      const jradegStr = r.querySelector("jradeg")?.textContent?.trim()
      const jdedegStr = r.querySelector("jdedeg")?.textContent?.trim()
      const jradeg =
        jradegStr && Number.isFinite(Number(jradegStr))
          ? Number(jradegStr)
          : undefined
      const jdedeg =
        jdedegStr && Number.isFinite(Number(jdedegStr))
          ? Number(jdedegStr)
          : undefined

      const ids = Array.from(r.querySelectorAll("id"))
        .map(n => n.textContent?.trim())
        .filter((v): v is string => !!v)

      const candidate: SesameCandidate = {
        resolver: resolverName,
        oname,
        otype,
        jpos,
        jradeg,
        jdedeg,
        ids: ids.length ? ids : undefined,
      }

      // Only include resolvers that produced something useful.
      if (candidate.oname || candidate.otype || candidate.jpos) {
        out.candidates.push(candidate)
      }
    }

    if (!out.candidates.length) out.resolved = false
    return out
  } catch {
    return out
  }
}

export function classifyOtypeToKind(otype?: string): SimbadKind {
  const o = (otype || "").trim()
  const low = o.toLowerCase()

  // Explicit rejects handled elsewhere, but keep fallback sane.
  if (!o) return "STAR"

  // Galaxies / AGN
  if (
    o === "G" ||
    low.includes("galaxy") ||
    low.includes("qso") ||
    low.includes("quasar") ||
    low.includes("agn") ||
    low.includes("seyfert")
  )
    return "DSO_GALAXY"

  // Clusters
  if (o === "GlCl" || low.includes("globular")) return "DSO_GLOBULAR_CLUSTER"
  if (o === "OpCl" || low.includes("open cluster")) return "DSO_OPEN_CLUSTER"
  if (o === "ClG" || low.includes("cluster of galaxies"))
    return "DSO_GALAXY_CLUSTER"

  // Nebulae
  if (o === "PN" || low.includes("planetary nebula")) return "DSO_PN"
  if (o === "SNR" || low.includes("supernova remnant")) return "DSO_SNR"
  if (o === "DkNeb" || low.includes("dark nebula")) return "DSO_DARK_NEBULA"
  if (o === "RfNeb" || low.includes("reflection nebula"))
    return "DSO_REFLECTION_NEBULA"
  if (o === "HII" || low.includes("hii") || low.includes("nebula"))
    return "DSO_NEBULA"

  // Molecular cloud / SFR-ish
  if (low.includes("mol") || low.includes("cloud") || low.includes("sfr"))
    return "DSO_MOL_CLOUD"

  // Stellar special cases
  if (o === "V*" || low.includes("variable")) return "STAR_VAR"
  if (o === "**" || low.includes("double") || low.includes("multiple"))
    return "STAR_MULT"

  // Asterism / generic “pattern”
  if (low.includes("asterism")) return "DSO_ASTERISM"

  // Default: single-star fallback (per your preference)
  return "STAR"
}

export function pickBestCandidate(
  resolved: SesameResolved,
): SesameCandidate | null {
  const candidates = (resolved?.candidates ?? []).filter(c => {
    if (!c) return false
    if (isSpacecraftOtype(c.otype)) return false
    if (!Number.isFinite(c.jradeg) || !Number.isFinite(c.jdedeg)) return false
    return true
  })
  if (!candidates.length) return null

  // Prefer SIMBAD resolver results.
  const simbad = candidates.find(c =>
    c.resolver.toLowerCase().includes("simbad"),
  )
  return simbad ?? candidates[0]
}

export function candidateToResolvedMatch(
  c: SesameCandidate,
  query?: string,
): SimbadResolvedMatch | null {
  if (!Number.isFinite(c.jradeg) || !Number.isFinite(c.jdedeg)) return null
  if (isSpacecraftOtype(c.otype)) return null

  // Reject candidates that don't have a proper name from the resolver
  // (Don't fall back to using the query string as the name)
  const name = c.oname?.trim() || c.ids?.[0]?.trim()
  if (!name) return null

  const kind = classifyOtypeToKind(c.otype)
  const raDeg = c.jradeg!
  const decDeg = c.jdedeg!
  const commonName = pickCommonName(c.ids, c.oname)

  // Stable enough for saved picks; avoids re-resolving.
  const recordNumber = `SIMBAD:${name}:${raDeg.toFixed(6)}:${decDeg.toFixed(6)}`

  return {
    recordNumber,
    name,
    commonName,
    kind,
    otype: c.otype,
    raDeg,
    decDeg,
    raHms: degreesToHms(raDeg),
    decDms: degreesToDms(decDeg),
    resolver: c.resolver,
  }
}

export function resolveBestMatch(
  query: string,
  resolved: SesameResolved,
): SimbadResolvedMatch | null {
  const best = pickBestCandidate(resolved)
  if (!best) return null
  return candidateToResolvedMatch(best, query)
}

export function buildMatches(
  resolved: SesameResolved,
  query?: string,
): SimbadResolvedMatch[] {
  return (resolved?.candidates ?? [])
    .map(c => candidateToResolvedMatch(c, query))
    .filter((m): m is SimbadResolvedMatch => !!m)
}

export function degreesToHms(raDeg: number): string {
  // RA: 0..360 -> hours
  let deg = raDeg % 360
  if (deg < 0) deg += 360
  const totalHours = deg / 15
  const h = Math.floor(totalHours)
  const mFloat = (totalHours - h) * 60
  const m = Math.floor(mFloat)
  const s = (mFloat - m) * 60
  const sFixed = s.toFixed(1).padStart(4, "0")
  return `${String(h).padStart(2, "0")} ${String(m).padStart(2, "0")} ${sFixed}`
}

export function degreesToDms(decDeg: number): string {
  const sign = decDeg < 0 ? "-" : "+"
  const abs = Math.abs(decDeg)
  const d = Math.floor(abs)
  const mFloat = (abs - d) * 60
  const m = Math.floor(mFloat)
  const s = (mFloat - m) * 60
  const sFixed = s.toFixed(1).padStart(4, "0")
  return `${sign}${String(d).padStart(2, "0")} ${String(m).padStart(2, "0")} ${sFixed}`
}

export function angularSeparationDeg(
  ra1Deg: number,
  dec1Deg: number,
  ra2Deg: number,
  dec2Deg: number,
): number {
  const ra1 = degToRad(ra1Deg)
  const dec1 = degToRad(dec1Deg)
  const ra2 = degToRad(ra2Deg)
  const dec2 = degToRad(dec2Deg)
  const cos =
    Math.sin(dec1) * Math.sin(dec2) +
    Math.cos(dec1) * Math.cos(dec2) * Math.cos(ra1 - ra2)
  const clamped = Math.min(1, Math.max(-1, cos))
  return radToDeg(Math.acos(clamped))
}

export function raDecToAltAzDeg(params: {
  raDeg: number
  decDeg: number
  latDeg: number
  lonDeg: number // east-positive, -180..180 preferred
  dateUtc: Date
}): { azDeg: number; altDeg: number } {
  const { raDeg, decDeg, latDeg, lonDeg, dateUtc } = params

  const lat = degToRad(latDeg)
  const ra = degToRad(raDeg)
  const dec = degToRad(decDeg)

  const lst = degToRad(localSiderealTimeDeg(dateUtc, lonDeg))
  let ha = lst - ra
  // Normalize hour angle to [-pi, pi]
  ha = ((ha + Math.PI) % (2 * Math.PI)) - Math.PI

  const sinAlt =
    Math.sin(dec) * Math.sin(lat) + Math.cos(dec) * Math.cos(lat) * Math.cos(ha)
  const alt = Math.asin(Math.min(1, Math.max(-1, sinAlt)))

  // Azimuth formula (0..360 from north, eastward)
  const y = -Math.sin(ha)
  const x = Math.tan(dec) * Math.cos(lat) - Math.sin(lat) * Math.cos(ha)
  let az = Math.atan2(y, x)
  if (az < 0) az += 2 * Math.PI

  return { azDeg: radToDeg(az), altDeg: radToDeg(alt) }
}

function localSiderealTimeDeg(dateUtc: Date, lonDeg: number): number {
  // Approximate GMST, then add longitude (east-positive).
  // Good enough for telescope pointing recommendations.
  const jd = julianDate(dateUtc)
  const d = jd - 2451545.0
  let gmst =
    280.46061837 +
    360.98564736629 * d +
    0.000387933 * (d / 36525) * (d / 36525) -
    ((d / 36525) * (d / 36525) * (d / 36525)) / 38710000
  gmst = ((gmst % 360) + 360) % 360
  let lst = gmst + lonDeg
  lst = ((lst % 360) + 360) % 360
  return lst
}

function julianDate(dateUtc: Date): number {
  return dateUtc.getTime() / 86400000 + 2440587.5
}

function degToRad(deg: number): number {
  return (deg * Math.PI) / 180
}

function radToDeg(rad: number): number {
  return (rad * 180) / Math.PI
}
